import { differenceWith, find, findIndex, get, isEqual, omit } from 'lodash';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { FIELD_PATHS, REDUX_STORE } from '@funnels/constants';
import {
  CreateApplicantAction,
  CustomerData,
  DeleteApplicantAction,
  FetchApplicantsAction,
  ReduxStoreData,
  ServerApplicantUpdate,
  StoreApplicant,
  UpdateApplicantAction,
  UpsertApplicantsAction,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import { getApplicantId, sortByCreatedDateAsc, waitForAuth } from '../_common';
import { updateCustomers } from '../customers/actions';
import {
  createApplicant,
  createApplicantFail,
  createApplicantSuccess,
  deleteApplicantFail,
  deleteApplicantSuccess,
  fetchApplicantsFail,
  fetchApplicantsSuccess,
  setApplicantError,
  updateApplicant,
  updateApplicantAbort,
  updateApplicantFail,
  updateApplicantSuccess,
  upsertApplicantsFail,
  upsertApplicantsSuccess,
} from './actions';
import ApplicantsAPI from './api';
import {
  fixApplicantDefaultValues,
  fixDependantsSharedWith,
  fixMaritalRelationship,
  mapApplicantDependantsIn,
  mapApplicantDependantsOut,
  mapApplicantRelationshipIn,
  mapApplicantRelationshipOut,
  trimApplicantSpaces,
} from './utils';

export function* createApplicantWorker(action: CreateApplicantAction): any {
  try {
    yield waitForAuth();
    const { applicationId, currentCustomerId, applicant, isClone = false, version } = action;

    [mapApplicantRelationshipOut, mapApplicantDependantsOut, fixApplicantDefaultValues, trimApplicantSpaces].forEach(
      (fn) => fn(applicant)
    );

    const { clientId, ...serverApplicant } = applicant;
    const serverCreateApplicant = isClone ? omit(serverApplicant, ['maritalDetails']) : serverApplicant;

    const response = yield call(ApplicantsAPI.create, applicationId, serverCreateApplicant, version);
    const defaultDependants = applicant.dependants || [];

    yield put(
      createApplicantSuccess(clientId, response.data.id, response.data.customerId, defaultDependants, currentCustomerId)
    );
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      applicant: { clientId },
      currentCustomerId,
    } = action;
    yield all([
      put(createApplicantFail(clientId, currentCustomerId)),
      put(setApplicantError(e.response?.data || e.message)),
    ]);
  }
}

export function* deleteApplicantWorker(action: DeleteApplicantAction): any {
  try {
    yield waitForAuth();
    const { applicationId, clientId, id, version } = action;
    const applicantId = yield getApplicantId(clientId, id);

    yield call(ApplicantsAPI.del, applicationId, applicantId, version);
    yield put(deleteApplicantSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    const { clientId } = action;
    yield all([put(deleteApplicantFail(clientId)), put(setApplicantError(e.response?.data || e.message))]);
  }
}

export function* fetchApplicantsWorker(action: FetchApplicantsAction): any {
  try {
    yield waitForAuth();
    const { applicationId, currentCustomerId, version } = action;
    const response = yield call(ApplicantsAPI.fetch, applicationId, version);
    const applicants = response.data.map((a: any) => {
      const applicant = omit(a, ['meta']) as StoreApplicant;

      [mapApplicantRelationshipIn, mapApplicantDependantsIn].forEach((fn) => fn(applicant));

      return applicant;
    });
    applicants.sort(sortByCreatedDateAsc);

    yield put(fetchApplicantsSuccess(applicants, currentCustomerId, version));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield all([put(fetchApplicantsFail()), put(setApplicantError(e.response?.data || e.message))]);
  }
}

export function* updateApplicantWorker(action: UpdateApplicantAction): any {
  try {
    yield waitForAuth();
    const { applicationId, currentCustomerId, applicant, version } = action;
    const applicantId = yield getApplicantId(applicant.clientId, applicant.id);

    // For whatever reason, the creation failed so we convert this update to a create
    if (!applicantId) {
      yield all([
        put(updateApplicantAbort()),
        put(createApplicant(applicationId, applicant, currentCustomerId, undefined, version)),
      ]);
    } else {
      const reduxStoreApplicants: StoreApplicant[] = yield select((state: ReduxStoreData) => state.applicants.data);

      [mapApplicantRelationshipOut, mapApplicantDependantsOut, fixApplicantDefaultValues, trimApplicantSpaces].forEach(
        (fn) => fn(applicant)
      );
      yield all(
        [fixMaritalRelationship, fixDependantsSharedWith].map((fn) => call(fn, applicant, reduxStoreApplicants))
      );

      const serverApplicant = omit(applicant, [
        'applicationId',
        'clientId',
        'createdAt',
        'id',
        'isDeleted',
        'isDeidentified',
        'deidentificationMeta',
        'updatedAt',
        'customerId',
        'invitationStatus',
      ]) as ServerApplicantUpdate;
      const response = yield call(ApplicantsAPI.update, applicationId, applicantId, serverApplicant, version);
      const defaultDependants = version === 2 ? response.data.dependants : response.data.dependants || [];
      yield put(updateApplicantSuccess(applicantId, defaultDependants, currentCustomerId));
    }
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      applicant: { clientId },
      currentCustomerId,
    } = action;
    yield all([
      put(updateApplicantFail(clientId, currentCustomerId)),
      put(setApplicantError(e.response?.data || e.message)),
    ]);
  }
}

export function* upsertApplicantsWorker(action: UpsertApplicantsAction) {
  try {
    yield waitForAuth();

    const { applicants, applicationId, currentCustomerId, version } = action;
    const reduxStoreApplicants: StoreApplicant[] = yield select((state: ReduxStoreData) => state.applicants.data);
    const changedCustomers: CustomerData[] = [];

    // Filter out server properties in the comparator function because its possible for that to be added by the store
    const changedApplicants = differenceWith(
      applicants,
      reduxStoreApplicants,
      ({ id: id1, customerId: cId1, ...rest1 }, { id: id2, customerId: cId2, ...rest2 }) => isEqual(rest1, rest2)
    );

    // Now we have to also check for those who have changed dependants
    applicants.forEach((a) => {
      const reduxStoreApplicant = find(reduxStoreApplicants, { clientId: a.clientId });

      if (reduxStoreApplicant) {
        const dependantsChanged = differenceWith(
          (a.dependants || [])
            .sort(({ age: age1 = 0 }, { age: age2 = 0 }) => age1 - age2)
            .map((d: any, i: number) => ({ ...d, index: i })),
          (reduxStoreApplicant.dependants || [])
            .sort(({ age: age1 = 0 }, { age: age2 = 0 }) => age1 - age2)
            .map((d, i) => ({ ...d, index: i })),
          isEqual
        );

        if (dependantsChanged.length > 0 && findIndex(changedApplicants, { clientId: a.clientId }) === -1) {
          changedApplicants.push(a);
        }

        const emailAddress = get({ applicants: [a] }, FIELD_PATHS.APPLICANT_EMAIL(0));
        const hasNewEmailAddress =
          emailAddress !== get({ applicants: [reduxStoreApplicant] }, FIELD_PATHS.APPLICANT_EMAIL(0));

        if (hasNewEmailAddress && a.customerId) {
          changedCustomers.push({ id: a.customerId, email: emailAddress });
        }
      }
    });

    yield all(
      changedApplicants
        .sort((a, b) => {
          if (!a.id) {
            return -1;
          } else if (!b.id) {
            return 1;
          }

          return 0;
        })
        .map((a) => {
          // We rely on grabbing the id from the store so that
          // we DONT rely on the store syncing to the form after initialisation
          const reduxStoreApplicant = find(reduxStoreApplicants, { clientId: a.clientId });
          if (!reduxStoreApplicant) {
            // No applicant
            return put(createApplicant(applicationId, a, currentCustomerId, undefined, version));
          } else if (!reduxStoreApplicant.id) {
            // Applicant but no id, therefore create has been fired but success hasnt returned
            return put(updateApplicant(applicationId, a, currentCustomerId, version));
          } else {
            // Applicant is in the store and has an id
            return put(
              updateApplicant(applicationId, { ...a, id: reduxStoreApplicant.id }, currentCustomerId, version)
            );
          }
        })
    );

    if (changedCustomers.length > 0) {
      yield put(updateCustomers(changedCustomers));
    }

    yield put(upsertApplicantsSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    yield all([put(upsertApplicantsFail()), put(setApplicantError(e.response?.data || e.message))]);
  }
}

// the 'watcher'
export function* applicantsWatcher() {
  yield takeEvery(REDUX_STORE.APPLICANTS.ACTION_TYPES.CREATE_APPLICANT, createApplicantWorker);
  yield takeEvery(REDUX_STORE.APPLICANTS.ACTION_TYPES.DELETE_APPLICANT, deleteApplicantWorker);
  yield takeEvery(REDUX_STORE.APPLICANTS.ACTION_TYPES.FETCH_APPLICANTS, fetchApplicantsWorker);
  yield takeEvery(REDUX_STORE.APPLICANTS.ACTION_TYPES.UPDATE_APPLICANT, updateApplicantWorker);
  yield takeEvery(REDUX_STORE.APPLICANTS.ACTION_TYPES.UPSERT_APPLICANTS, upsertApplicantsWorker);
}

export default applicantsWatcher;
